/**
 * URL flags for testing. Read once at boot; changing them needs a reload.
 *
 *   ?webgl        force the WebGL 2 backend (the fallback path), even where WebGPU exists
 *   ?dpr=1.5      cap the device pixel ratio (default: min(devicePixelRatio, 2))
 *   ?stress=200   add N extra instanced pillars to load the GPU
 *   ?hud=0        hide the debug overlay
 *   ?orbit=0      lock the camera (no drag controls)
 *   ?tone=agx     tone mapper: neutral (default), agx, aces, none.
 *                 AgX shifts RTK crimson toward salmon; Neutral keeps it red. Final choice at G2.
 *   ?post=0       skip the post pipeline entirely (plain renderer.render), for cost comparison
 *   ?bloom=0.6    bloom strength; 0 removes the bloom pass
 *   ?bloomr=0.2   bloom radius 0..1 (how far the glow spreads)
 *   ?bloomsrc=all bloom source: masked (default; artworks never bloom) or all (everything bright)
 *   ?grain=0.07   film grain amount; 0 removes the grain node
 *   ?quality=auto adaptive quality (default): start at L0, step down until 60 fps holds.
 *                 ?quality=3 pins a ladder level (0 best … 6 grain only), no auto.
 *   ?scale=0.75   render scale on top of the base dpr (pins quality, auto off)
 *   ?msaa=0       scene-pass MSAA: 4 or 0 (0 adds FXAA) (pins quality, auto off)
 *   ?bloomres=0.25 bloom internal resolution, 0.1..1 (pins quality, auto off)
 *   ?qfail=55     test only: the fps below which auto quality steps down (default 55).
 *                 Lets headless software rendering (~3 fps) exercise the runtime steps.
 */
const params = new URLSearchParams(window.location.search)

function num(name: string, fallback: number): number {
  const raw = params.get(name)
  if (raw === null) return fallback
  const n = Number(raw)
  return Number.isFinite(n) ? n : fallback
}

function bool(name: string, fallback: boolean): boolean {
  if (!params.has(name)) return fallback
  const raw = params.get(name)
  return raw === '' || raw === '1' || raw === 'true'
}

export const flags = {
  forceWebGL: bool('webgl', false),
  maxDpr: Math.max(0.5, Math.min(num('dpr', 2), 3)),
  stress: Math.max(0, Math.min(Math.floor(num('stress', 0)), 5000)),
  hud: bool('hud', true),
  orbit: bool('orbit', true),
  tone: (['neutral', 'agx', 'aces', 'none'] as const).find((t) => t === params.get('tone')) ?? 'neutral',
  post: bool('post', true),
  bloom: Math.max(0, Math.min(num('bloom', 0.6), 5)),
  bloomRadius: Math.max(0, Math.min(num('bloomr', 0.2), 1)),
  bloomSource: (['masked', 'all'] as const).find((s) => s === params.get('bloomsrc')) ?? 'masked',
  grain: Math.max(0, Math.min(num('grain', 0.07), 0.5)),
  quality: quality(),
  scale: params.has('scale') ? Math.max(0.25, Math.min(num('scale', 1), 1)) : null,
  msaa: params.has('msaa') ? (num('msaa', 4) >= 2 ? 4 : 0) : null,
  qualityFailFps: Math.max(1, Math.min(num('qfail', 55), 240)),
  bloomRes: params.has('bloomres') ? Math.max(0.1, Math.min(num('bloomres', 0.5), 1)) : null,
} as const

function quality(): 'auto' | number {
  const raw = params.get('quality')
  if (raw === null || raw === 'auto') return 'auto'
  if (raw === 'max' || raw === 'off') return 0
  const n = Math.floor(Number(raw))
  return Number.isFinite(n) ? Math.max(0, Math.min(n, 6)) : 'auto'
}

/** WebGPU needs a secure context (https or localhost) and navigator.gpu. */
export const webgpuAvailable =
  typeof navigator !== 'undefined' && 'gpu' in navigator && window.isSecureContext
